/**
 * Column definitions for the Withdraw management table.
 * Extracted to keep Withdraw.tsx under 200 lines.
 */
import { Tag, Tooltip } from "antd";
import { Link } from "react-router-dom";
import { format, fromUnixTime } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faWarning, faCopy, faFilter } from "@fortawesome/free-solid-svg-icons";
import formatMoney from "../../../utils/formatMoney";
import { Payment } from "./Withdraw";

const STATUS_TAG: Record<number, { label: string; color: string }> = {
    1: { label: "Chờ duyệt", color: "orange" },
    2: { label: "Đã xử lý", color: "green" },
    3: { label: "Đã huỷ", color: "default" },
};

export function getWithdrawColumns(
    handleOpenUpdatePayment: (p: Payment, type: "complete" | "cancell") => void,
    onFilterByUser: (userId: string) => void
) {
    return [
        {
            title: "Request ID",
            dataIndex: "paymentId",
            key: "paymentId",
            width: 90,
            render: (_: any, { paymentId }: Payment) => (
                <span className="font-mono text-content-3 text-xs">#{paymentId}</span>
            ),
        },
        {
            title: "User",
            dataIndex: "user",
            key: "user",
            width: 150,
            render: (_: any, { user }: Payment) => (
                <div className="text-sm leading-tight">
                    <Tooltip title="Lọc theo user này">
                        <button
                            onClick={() => onFilterByUser(String(user.userId))}
                            className="font-medium text-left hover:text-blue-600 transition-colors flex items-center gap-1 group"
                        >
                            <span className="truncate max-w-[110px] block">{user.name || "—"}</span>
                            <FontAwesomeIcon
                                icon={faFilter}
                                className="text-xs text-content-3 group-hover:text-blue-500 flex-shrink-0"
                            />
                        </button>
                    </Tooltip>
                    <div className="text-content-3">ID: {user.userId}</div>
                </div>
            ),
        },
        {
            title: "Tài khoản",
            dataIndex: "bankAccountName",
            key: "bankAccountName",
            width: 200,
            render: (_: any, { bankAccountName, bankAccountNumber }: Payment) => (
                <div className="text-sm leading-tight">
                    <div className="font-medium">{bankAccountName}</div>
                    <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-content-3 font-mono text-xs">{bankAccountNumber}</span>
                        <Tooltip title="Copy số tài khoản">
                            <button
                                onClick={() => navigator.clipboard?.writeText(bankAccountNumber)}
                                className="text-content-3 hover:text-blue-500 transition-colors flex-shrink-0"
                            >
                                <FontAwesomeIcon icon={faCopy} className="text-xs" />
                            </button>
                        </Tooltip>
                    </div>
                </div>
            ),
        },
        {
            title: "Trạng thái",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (_: any, { status, paymentId }: Payment) => {
                const tag = STATUS_TAG[status] || { label: String(status), color: "default" };
                return (
                    <div className="flex flex-col gap-1">
                        <Tag color={tag.color}>{tag.label}</Tag>
                        {status === 2 && (
                            <Link
                                to={`${paymentId}`}
                                className="text-xs text-blue-500 hover:underline whitespace-nowrap"
                            >
                                Xem chi tiết →
                            </Link>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Ngày tạo",
            dataIndex: "createTime",
            key: "createTime",
            width: 130,
            render: (_: any, { createTime }: Payment) =>
                createTime ? (
                    <span className="text-xs text-content-2 whitespace-nowrap">
                        {format(fromUnixTime(createTime), "dd/MM/yyyy HH:mm")}
                    </span>
                ) : "—",
        },
        {
            title: "Ngày xử lý",
            dataIndex: "completeTime",
            key: "completeTime",
            width: 130,
            render: (_: any, { completeTime }: Payment) =>
                completeTime ? (
                    <span className="text-xs text-content-2 whitespace-nowrap">
                        {format(fromUnixTime(completeTime), "dd/MM/yyyy HH:mm")}
                    </span>
                ) : "—",
        },
        {
            title: "Ghi chú",
            dataIndex: "note",
            key: "note",
            width: 160,
        },
        {
            title: "Admin",
            dataIndex: "admin",
            key: "admin",
            width: 80,
            render: (_: any, { admin }: Payment) =>
                admin ? `#${admin.userId}` : "—",
        },
        {
            title: "Số tiền",
            dataIndex: "amount",
            key: "amount",
            width: 130,
            fixed: "right" as const,
            render: (_: any, el: Payment) => {
                // Red highlight if check shows discrepancy
                const totalWithdrawn = (el.totalAmountWithdraw || 0) + el.amount;
                const totalAvailable = (el.totalCommission || 0) + (el.bonus || 0);
                const isOver = el.status === 1 && totalWithdrawn > totalAvailable;
                return (
                    <span className={`font-semibold text-sm ${isOver ? "text-red-600" : "text-pink-600"}`}>
                        {formatMoney.format(el.amount)}
                    </span>
                );
            },
        },
        {
            title: "Check",
            key: "check",
            width: 150,
            fixed: "right" as const,
            render: (_: any, el: Payment) => {
                if (el.status !== 1) {
                    return <FontAwesomeIcon className="text-content-3" icon={faCheckCircle} />;
                }
                const totalWithdrawn = (el.totalAmountWithdraw || 0) + el.amount;
                const totalAvailable = (el.totalCommission || 0) + (el.bonus || 0);
                const isOver = totalWithdrawn > totalAvailable;
                return (
                    <div className="flex flex-col items-start text-xs gap-0.5">
                        <FontAwesomeIcon
                            icon={isOver ? faWarning : faCheckCircle}
                            className={isOver ? "text-red-500" : "text-green-500"}
                        />
                        <span className={isOver ? "text-red-600 font-semibold" : "text-green-500"}>
                            {formatMoney.format(totalWithdrawn)} /{" "}
                            {formatMoney.format(totalAvailable)}
                        </span>
                    </div>
                );
            },
        },
        {
            title: "Thao tác",
            key: "action",
            width: 140,
            fixed: "right" as const,
            render: (_: any, el: Payment) => (
                <div className="flex gap-1.5">
                    <button
                        disabled={el.status !== 1}
                        onClick={() => handleOpenUpdatePayment(el, "complete")}
                        className="px-2 py-1 text-xs bg-pink-600 text-white rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-pink-700 transition-colors"
                    >
                        Thanh toán
                    </button>
                    <button
                        disabled={el.status !== 1}
                        onClick={() => handleOpenUpdatePayment(el, "cancell")}
                        className="px-2 py-1 text-xs bg-slate-700 text-white rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-800 transition-colors"
                    >
                        Huỷ
                    </button>
                </div>
            ),
        },
    ];
}
